// ============================================================================
// Instagram DM — Graph / Messaging API response shapes + error classification.
//
// Instagram Messaging sits on top of Meta's Graph API (Graph v21+). The wire
// shapes closely mirror Facebook Messenger BUT error codes + operational
// constraints differ:
//
//   - 24-hour messaging window: business may freely reply within 24h of the
//     user's last inbound message. Outside that window Meta rejects the POST
//     unless it carries `messaging_type: "MESSAGE_TAG"` + `tag: "HUMAN_AGENT"`.
//     Meta enforces this with error codes 10303/10304 (subcode 2534022 /
//     2534023) depending on which tier of the window was breached.
//   - Standalone Instagram Login (2024 migration). User access tokens are
//     IG-issued — distinct namespace from the FB user token.
//
// We classify the most common failure modes into a discriminated error class
// so the dispatcher + chat inbox can react without re-parsing Meta's subcode
// matrix:
//
//   - AUTH_FAILED       → the token is wrong or the IG account is not linked
//                          to an authorized app install
//   - TOKEN_EXPIRED     → long-lived IG token past its 60-day horizon
//   - RATE_LIMITED      → hit Meta's app-level / user-level rate limit
//   - OUTSIDE_24H_WINDOW→ attempted free-form reply > 24h after last inbound
//   - MESSAGE_TAG_REQUIRED → caller did not supply MESSAGE_TAG when required
//   - USER_UNAVAILABLE  → IGSID no longer reachable (blocked / deactivated)
//   - INVALID_PAYLOAD   → programmer error (empty recipient, bad shape)
//   - PROVIDER_ERROR    → everything else (5xx, unexpected envelope)
// ============================================================================

// ============================================================================
// Wire shapes — the minimum slice of the Graph API schema we consume.
// ============================================================================

export interface InstagramSendResponse {
  recipient_id: string
  message_id: string
}

export interface InstagramUserProfile {
  /** IGSID echoed back. */
  id: string
  /** Display name on the IG profile (may be empty for privacy reasons). */
  name?: string
  /** IG @-handle (no leading @). */
  username?: string
  /** Short-lived CDN URL for the profile picture. Do not cache long-term. */
  profile_pic?: string
}

export interface InstagramGraphError {
  message: string
  type: string
  code: number
  error_subcode?: number
  fbtrace_id?: string
}

export interface InstagramErrorEnvelope {
  error: InstagramGraphError
}

// ============================================================================
// InstagramDmError — discriminated by `code` so `switch` stays exhaustive.
// ============================================================================

export type InstagramDmErrorCode =
  | 'AUTH_FAILED'
  | 'TOKEN_EXPIRED'
  | 'USER_REVOKED'
  | 'RATE_LIMITED'
  | 'PROVIDER_ERROR'
  | 'INVALID_PAYLOAD'
  | 'USER_UNAVAILABLE'
  | 'OUTSIDE_24H_WINDOW'
  | 'MESSAGE_TAG_REQUIRED'

export interface InstagramDmErrorDetails {
  /** Raw Meta code (if we got one from the envelope). */
  providerCode?: number
  /** Raw Meta subcode (if any). */
  providerSubcode?: number
  /** Meta trace id — useful for support tickets + Meta Help Center filings. */
  fbtraceId?: string
  /** HTTP status of the underlying request, if known. */
  httpStatus?: number
  /** Parsed Retry-After (ms) — only populated for RATE_LIMITED. */
  retryAfterMs?: number
}

export class InstagramDmError extends Error {
  public readonly name = 'InstagramDmError'
  constructor(
    public readonly code: InstagramDmErrorCode,
    message: string,
    public readonly details: InstagramDmErrorDetails = {},
  ) {
    super(message)
  }
}

/**
 * Thrown when webhook signature verification fails AT THE CALLER LEVEL.
 * The webhook adapter itself returns `false` from `verifyWebhook` rather
 * than throwing — this class exists for callers that want to raise on a
 * bad signature directly (e.g. admin-tool replays).
 */
export class InstagramDmSignatureError extends Error {
  public readonly name = 'InstagramDmSignatureError'
  constructor(message: string) {
    super(message)
  }
}

// ============================================================================
// Helpers — classify / detect
// ============================================================================

/**
 * Parse a `Retry-After` header value (either delta-seconds or HTTP-date) and
 * return milliseconds. Returns `undefined` on unparseable input so callers
 * can fall back to a default backoff strategy.
 */
export function parseInstagramRetryAfterMs(
  headerValue: string | null | undefined,
): number | undefined {
  if (!headerValue) return undefined
  const trimmed = headerValue.trim()
  if (trimmed.length === 0) return undefined
  const asNumber = Number(trimmed)
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return Math.round(asNumber * 1000)
  }
  const parsed = Date.parse(trimmed)
  if (Number.isFinite(parsed)) {
    const diff = parsed - Date.now()
    return diff > 0 ? diff : 0
  }
  return undefined
}

/**
 * Map Meta's Graph API error envelope + HTTP status to an `InstagramDmError`.
 *
 * Decision tree (order matters — first match wins):
 *   1. HTTP 401                          → AUTH_FAILED
 *   2. code 190 + subcode 463            → TOKEN_EXPIRED (IG long-lived expired)
 *   3. code 190                          → AUTH_FAILED (OAuth token invalid)
 *   4. HTTP 429 OR subcode 2018 OR
 *      subcode 613                       → RATE_LIMITED
 *   5. subcode 10303 / 10304 / 2534022 / 2534023  → OUTSIDE_24H_WINDOW
 *   6. code 100 + subcode 2018108        → MESSAGE_TAG_REQUIRED (tag mismatch)
 *   7. subcode 2018001 / 551             → USER_UNAVAILABLE (blocked / deactivated)
 *   8. providerCode 1/2/unspec + 5xx     → PROVIDER_ERROR
 *   9. default                            → PROVIDER_ERROR
 *
 * References:
 *   - https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/messaging-api/error-handling
 *   - https://developers.facebook.com/docs/messenger-platform/error-codes (shared w/ IG)
 */
export function classifyInstagramError(
  envelope: InstagramErrorEnvelope | null | undefined,
  httpStatus: number,
  retryAfterMs?: number,
): InstagramDmError {
  const graphError = envelope?.error
  const providerCode = graphError?.code
  const providerSubcode = graphError?.error_subcode
  const fbtraceId = graphError?.fbtrace_id
  const rawMessage =
    graphError?.message ?? `Instagram Graph API error (HTTP ${httpStatus})`
  const message = sanitizeInstagramErrorMessage(rawMessage)

  const details: InstagramDmErrorDetails = {
    providerCode,
    providerSubcode,
    fbtraceId,
    httpStatus,
    retryAfterMs,
  }

  // 1. OAuth errors — Meta uses providerCode 190 for all access-token failures.
  //    Meta ALSO sends HTTP 401 on these, so we MUST inspect the provider
  //    subcode BEFORE bucketing on status — otherwise subcode 463/467
  //    ("long-lived token expired") collapses into AUTH_FAILED and the refresh
  //    coordinator never triggers an auto-rotate. (Round 1 H2 fix.)
  //
  //    Subcode semantics (Meta Graph error catalog, Issue #249 M2):
  //      - 463: "Session has expired" — long-lived token past its 60-day
  //             horizon. Recoverable via the refresh endpoint.
  //      - 467: "Access token has expired due to password change or
  //             session duration" — also refresh-recoverable.
  //      - 460: "The session has been invalidated because the user
  //             changed their password or Facebook has changed the session
  //             for security reasons". NOT recoverable via refresh — the user
  //             must re-auth (fresh OAuth code exchange).
  //      - 458: "The user has not authorized application" / "App not
  //             installed on user's account". User removed the app from
  //             their IG settings — we surface as USER_REVOKED so the
  //             dispatcher marks the instance `disconnected` without
  //             attempting token refresh.
  if (providerCode === 190) {
    if (providerSubcode === 463 || providerSubcode === 467) {
      return new InstagramDmError('TOKEN_EXPIRED', message, details)
    }
    if (providerSubcode === 458) {
      // App not installed / authorization removed — the only fix is for the
      // user to re-auth. Token refresh will keep failing until that happens.
      return new InstagramDmError('USER_REVOKED', message, details)
    }
    if (providerSubcode === 460) {
      // Password-changed / forced re-login — refresh WILL fail; surface as
      // AUTH_FAILED so the dispatcher routes to the re-auth UX rather than
      // the refresh coordinator's retry loop.
      return new InstagramDmError('AUTH_FAILED', message, details)
    }
    return new InstagramDmError('AUTH_FAILED', message, details)
  }

  // 2. HTTP 401 without code 190 — bad / missing token at the HTTP layer.
  if (httpStatus === 401) {
    return new InstagramDmError('AUTH_FAILED', message, details)
  }

  // 4. Rate limiting — HTTP 429 OR Graph-level app-request-limit subcode
  // (2018) OR user-throttle subcode 613. The subcode can surface on a 200-
  // coded envelope too, so check independent of HTTP status.
  if (
    httpStatus === 429 ||
    providerSubcode === 2018 ||
    providerSubcode === 613
  ) {
    return new InstagramDmError('RATE_LIMITED', message, details)
  }

  // 5. 24h-window violations. Multiple subcodes point at the same operational
  // cause (Meta changed the codes between v15→v21 without deprecating the old
  // ones). We list every variant we have seen in the wild.
  if (
    providerSubcode === 10303 ||
    providerSubcode === 10304 ||
    providerSubcode === 2534022 ||
    providerSubcode === 2534023
  ) {
    return new InstagramDmError('OUTSIDE_24H_WINDOW', message, details)
  }

  // 6. MESSAGE_TAG required but missing / invalid.
  // code 100 + subcode 2018108 is "Message Tag Mismatch" — caller supplied a
  // tag Meta does not allow for Instagram (IG only supports HUMAN_AGENT).
  if (providerCode === 100 && providerSubcode === 2018108) {
    return new InstagramDmError('MESSAGE_TAG_REQUIRED', message, details)
  }

  // 7. User-side unavailability — blocked, deactivated, or restricted account.
  if (providerSubcode === 2018001 || providerSubcode === 551) {
    return new InstagramDmError('USER_UNAVAILABLE', message, details)
  }

  // 8 / 9. Default — anything else is an upstream provider error.
  return new InstagramDmError('PROVIDER_ERROR', message, details)
}

/**
 * Strip Meta-shaped secrets out of an error / log message before it leaves
 * the adapter. Matches the token / secret patterns Meta commonly echoes back
 * in its verbose error envelopes ("Tried to fetch with access_token=EAA...",
 * etc.). Kept co-located with the classifier so error messages sanitise in
 * one hop; see `oauth.ts#redactInstagramSecrets` for a broader variant used
 * by the OAuth code path.
 */
export function sanitizeInstagramErrorMessage(raw: string): string {
  return raw
    .replace(/(access_token=)[^&\s"']+/gi, '$1REDACTED')
    .replace(/(client_secret=)[^&\s"']+/gi, '$1REDACTED')
    .replace(/(ig_exchange_token=)[^&\s"']+/gi, '$1REDACTED')
    .replace(/(ig_refresh_token=)[^&\s"']+/gi, '$1REDACTED')
    .replace(/(Bearer\s+)[A-Za-z0-9._\-|]+/gi, '$1REDACTED')
    .replace(/\bEAA[A-Za-z0-9_\-]{20,}/g, 'REDACTED')
    .replace(/\bIGQ[A-Za-z0-9_\-]{20,}/g, 'REDACTED')
    .replace(/[A-Za-z0-9+/=_\-]{40,}/g, 'REDACTED')
}
